import { z } from 'zod';

import { FilterConfigSchema, SortConfigSchema } from './filterAndSortConfig.js';
import { type ConsumerOfPdsDocumentMetadata, type ConsumerOfPdsEncounters } from './flowTypes.js';
import { isDefined } from '../../../utils.js';
import {
  BaseFlowBlockSchema,
  BlockType,
  ConsumerOfPatientSchema,
  ConsumerOfProviderSchema,
  DurationUnits,
  type JustSourceBlockName,
  ProducedType,
  returnTypeSchema,
} from '../base/baseSchema.js';

export * as FilterAndSortConfig from './filterAndSortConfig.js';

const stringTrimEmptyAsUndefined = () =>
  z.string().transform((val) => {
    const trimmed = val.trim();
    return trimmed === '' ? undefined : trimmed;
  });

export enum PatientDataStoreEndpoint {
  SurgicalHistories = 'SurgicalHistories',
  Medications = 'Medications',
  Encounters = 'Encounters',
  LabValues = 'LabValues',
  Conditions = 'Conditions',
  DocumentMetadata = 'DocumentMetadata',
  DocumentSearch = 'DocumentSearch',
  DocumentTranscriptions = 'DocumentTranscriptions',
  Practitioners = 'Practitioners',
  BookedAppointments = 'BookedAppointments',
  Orders = 'Orders',
}

// TODO: These should be generated from the schema
// and shared between the client and the server, but it's too hard now.
export const SURGICAL_HISTORY_STATUS_OPTIONS = [
  'in-progress',
  'on-hold',
  'stopped',
  'completed',
] as const;
export const MEDICATION_STATUS_OPTIONS = ['active', 'on-hold', 'completed', 'stopped'] as const;
export const CONDITION_CLINICAL_STATUS_OPTIONS = [
  'active',
  'inactive',
  'recurrence',
  'relapse',
  'remission',
  'resolved',
  'unknown',
] as const;
export const CONDITION_VERIFICATION_STATUS_OPTIONS = [
  'confirmed',
  'differential',
  'entered-in-error',
  'provisional',
  'refuted',
  'unconfirmed',
] as const;
export const CONDITION_KIND_OPTIONS = ['ENCOUNTER_DIAGNOSIS', 'PROBLEM_LIST'] as const;
export const ORDER_STATUS_OPTIONS = ['active', 'revoked', 'completed'] as const;

export const DURATION_UNIT_OPTIONS = [
  DurationUnits.Years,
  DurationUnits.Weeks,
  DurationUnits.Days,
] as const;

const DurationSchema = z.object({
  value: z.number().positive().int(),
  units: z.enum(DURATION_UNIT_OPTIONS),
});

const ConsumerPdsDocumentMetadataSchema = z
  .object({
    sourceBlockName: z.string(),
  })
  .transform(({ sourceBlockName }) => ({
    sourceBlockName,
    consumesType: ProducedType.PdsDocumentMetadata,
  })) as unknown as z.ZodType<ConsumerOfPdsDocumentMetadata, JustSourceBlockName>;

const ConsumerPdsEncountersSchema = z
  .object({
    // Don't require consumesType in the input payload, we hardcode it
    sourceBlockName: z.string(),
  })
  .transform(({ sourceBlockName }) => ({
    sourceBlockName,
    consumesType: ProducedType.PdsEncounters,
  })) as unknown as z.ZodType<ConsumerOfPdsEncounters, JustSourceBlockName>;

const BasePatientDataStoreBlockConfigSchema = z.object({
  endpoint: z.enum(PatientDataStoreEndpoint),
  patient: ConsumerOfPatientSchema({ isRequired: false }),
  baseFilters: z
    .object({
      filterConfig: FilterConfigSchema.optional(),
      sortConfig: SortConfigSchema.optional(),
      limit: z
        .union([z.number(), z.string()])
        .transform((val) => (val ? Number(val) : undefined))
        .refine((val) => val === undefined || val >= 0, 'Limit must be a non-negative number')
        .optional(),
    })
    .optional(),
});

export const SurgicalHistoriesConfigSchema = BasePatientDataStoreBlockConfigSchema.extend({
  endpoint: z.literal(PatientDataStoreEndpoint.SurgicalHistories),
  surgicalHistoryFilters: z
    .object({
      status: z.enum(SURGICAL_HISTORY_STATUS_OPTIONS).optional(),
    })
    .optional(),
  returnType: returnTypeSchema(ProducedType.PdsSurgicalHistories),
});

export const MedicationsConfigSchema = BasePatientDataStoreBlockConfigSchema.extend({
  endpoint: z.literal(PatientDataStoreEndpoint.Medications),
  encounters: ConsumerPdsEncountersSchema.optional(),
  medicationFilters: z
    .object({
      status: z.enum(MEDICATION_STATUS_OPTIONS).optional(),
    })
    .optional(),
  returnType: returnTypeSchema(ProducedType.PdsMedications),
});

export const ConditionConfigSchema = BasePatientDataStoreBlockConfigSchema.extend({
  endpoint: z.literal(PatientDataStoreEndpoint.Conditions),
  encounters: ConsumerPdsEncountersSchema.optional(),
  conditionFilters: z
    .object({
      clinicalStatus: z.enum(CONDITION_CLINICAL_STATUS_OPTIONS).optional(),
      verificationStatus: z.enum(CONDITION_VERIFICATION_STATUS_OPTIONS).optional(),
      kind: z.enum(CONDITION_KIND_OPTIONS).optional(),
      encounterLookbackDuration: DurationSchema.optional(),
    })
    .optional(),
  returnType: returnTypeSchema(ProducedType.PdsConditions),
});

export const EncountersConfigSchema = BasePatientDataStoreBlockConfigSchema.extend({
  endpoint: z.literal(PatientDataStoreEndpoint.Encounters),
  encounterFilters: z.object({ lookbackDuration: DurationSchema.optional() }).optional(),
  returnType: returnTypeSchema(ProducedType.PdsEncounters),
});

export const LabValuesConfigSchema = BasePatientDataStoreBlockConfigSchema.extend({
  endpoint: z.literal(PatientDataStoreEndpoint.LabValues),
  encounters: ConsumerPdsEncountersSchema.optional(),
  labValueFilters: z
    .object({
      lookbackDuration: DurationSchema.optional(),
    })
    .optional(),
  returnType: returnTypeSchema(ProducedType.PdsLabValues),
});

export const DocumentMetadataConfigSchema = BasePatientDataStoreBlockConfigSchema.extend({
  endpoint: z.literal(PatientDataStoreEndpoint.DocumentMetadata),
  encounters: ConsumerPdsEncountersSchema.optional(),
  documentMetadataFilters: z
    .object({
      documentType: stringTrimEmptyAsUndefined().optional(),
      documentTitle: stringTrimEmptyAsUndefined().optional(),
      lookbackDuration: DurationSchema.optional(),
    })
    .optional(),
  returnType: returnTypeSchema(ProducedType.PdsDocumentMetadata),
});

export const DocumentTranscriptionsConfigSchema = BasePatientDataStoreBlockConfigSchema.extend({
  endpoint: z.literal(PatientDataStoreEndpoint.DocumentTranscriptions),
  returnType: returnTypeSchema(ProducedType.PdsDocumentTranscriptions),
  documentMetadata: ConsumerPdsDocumentMetadataSchema,
});

export type PdsDocumentTranscriptionsConfig = z.infer<typeof DocumentTranscriptionsConfigSchema>;

export const DocumentSearchConfigSchema = BasePatientDataStoreBlockConfigSchema.extend({
  endpoint: z.literal(PatientDataStoreEndpoint.DocumentSearch),
  returnType: returnTypeSchema(ProducedType.PdsDocumentSearchResults),
  searchQuery: stringTrimEmptyAsUndefined(),
});

export const PractitionersConfigSchema = BasePatientDataStoreBlockConfigSchema.extend({
  endpoint: z.literal(PatientDataStoreEndpoint.Practitioners),
  returnType: returnTypeSchema(ProducedType.PdsPractitioners),
  practitionerFilters: z
    .object({
      npi: stringTrimEmptyAsUndefined().optional(),
      provider: ConsumerOfProviderSchema.optional(),
    })
    .refine(
      (data) => isDefined(data.provider) || isDefined(data.npi),
      'Either provider or npi must be set for practitioner search'
    ),
});

export const BookedAppointmentsConfigSchema = BasePatientDataStoreBlockConfigSchema.extend({
  endpoint: z.literal(PatientDataStoreEndpoint.BookedAppointments),
  returnType: returnTypeSchema(ProducedType.PdsBookedAppointments),
  bookedAppointmentFilters: z
    .object({
      // Use custom schema to allow negative values
      after: z
        .object({
          value: z.number().int(),
          units: z.enum(DURATION_UNIT_OPTIONS),
        })
        .optional(),
    })
    .optional(),
});

export const OrdersConfigSchema = BasePatientDataStoreBlockConfigSchema.extend({
  endpoint: z.literal(PatientDataStoreEndpoint.Orders),
  returnType: returnTypeSchema(ProducedType.PdsOrders),
  orderFilters: z.object({
    provider: ConsumerOfProviderSchema.optional(),
    encounters: ConsumerPdsEncountersSchema.optional(),
    status: z.enum(ORDER_STATUS_OPTIONS).optional(),
  }),
});

export const PatientDataStoreConfigSchema = z.discriminatedUnion('endpoint', [
  SurgicalHistoriesConfigSchema,
  MedicationsConfigSchema,
  EncountersConfigSchema,
  LabValuesConfigSchema,
  ConditionConfigSchema,
  DocumentMetadataConfigSchema,
  DocumentSearchConfigSchema,
  DocumentTranscriptionsConfigSchema,
  PractitionersConfigSchema,
  BookedAppointmentsConfigSchema,
  OrdersConfigSchema,
]);

export type PatientDataStoreBlockConfig = z.infer<typeof PatientDataStoreConfigSchema>;

export const PatientDataStoreBlockSchema = BaseFlowBlockSchema.extend({
  type: z.literal(BlockType.PatientDataStore),
  config: PatientDataStoreConfigSchema,
});

export type PatientDataStoreBlock = z.infer<typeof PatientDataStoreBlockSchema>;
